import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLatestFunc } from './useLatestFunc';
import { getNormalizedRange, isPositionWithinBounds, max, min } from '../utils';
import type { CellRangeSelection, Position } from '../types';
import type { EditCellState, SelectCellState } from './useSelectedPosition';

interface UseRangeSelectionArgs<R> {
  enabled: boolean;
  rows: readonly R[];
  maxColIdx: number;
  gridRef: React.RefObject<HTMLDivElement>;
  selectedPosition: SelectCellState | EditCellState<R>;
  selectedCellIsWithinViewportBounds: boolean;
  selectCell: (position: Position, enableEditor?: boolean, scrollToCell?: boolean) => void;
  scrollToCell: (position: Position) => void;
  onSelectedRangeChange: ((range: CellRangeSelection | null) => void) | undefined;
}

/**
 * Excel-like rectangular range selection. The selected cell is the range's anchor,
 * so only the head is stored; the range collapses whenever the selection changes
 * (via `collapseRange` in `selectCell`) or stops being a plain cell selection.
 */
export function useRangeSelection<R>({
  enabled,
  rows,
  maxColIdx,
  gridRef,
  selectedPosition,
  selectedCellIsWithinViewportBounds,
  selectCell,
  scrollToCell,
  onSelectedRangeChange,
}: UseRangeSelectionArgs<R>) {
  const [rangeHead, setRangeHead] = useState<Position | null>(null);
  const [isRangeDragging, setIsRangeDragging] = useState(false);
  const dragStartRef = useRef<Position | null>(null);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);

  // Drop the head (and immediately treat it as gone) when the range can no longer
  // exist as a plain in-bounds cell selection: the anchor stopped being a SELECT
  // in bounds (editor opened bypassing selectCell, rows/columns removed), or the
  // head collapsed back onto the anchor — a single-cell range is indistinguishable
  // from no range. Same render-time reinit pattern as useSelectedPosition.
  let resolvedHead = rangeHead;
  if (
    resolvedHead !== null &&
    (!(
      enabled &&
      selectedPosition.mode === 'SELECT' &&
      isPositionWithinBounds(selectedPosition, maxColIdx, rows.length) &&
      isPositionWithinBounds(resolvedHead, maxColIdx, rows.length)
    ) ||
      (resolvedHead.idx === selectedPosition.idx && resolvedHead.rowIdx === selectedPosition.rowIdx))
  ) {
    setRangeHead(null);
    resolvedHead = null;
  }

  const { idx: anchorIdx, rowIdx: anchorRowIdx } = selectedPosition;
  const normalizedRange = useMemo(
    () => (resolvedHead === null ? null : getNormalizedRange({ idx: anchorIdx, rowIdx: anchorRowIdx }, resolvedHead)),
    [resolvedHead, anchorIdx, anchorRowIdx],
  );

  const prevRangeKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const rangeKey = normalizedRange
      ? `${normalizedRange.startIdx}:${normalizedRange.startRowIdx}:${normalizedRange.endIdx}:${normalizedRange.endRowIdx}`
      : null;
    if (rangeKey === prevRangeKeyRef.current) return;
    prevRangeKeyRef.current = rangeKey;
    onSelectedRangeChange?.(normalizedRange);
  });

  const collapseRange = useCallback(() => {
    setRangeHead(null);
  }, []);

  function startWindowMouseTracking(): void {
    window.addEventListener('mouseover', onMouseOver);
    window.addEventListener('mouseup', onMouseUp);

    function onMouseOver(event: MouseEvent): void {
      // Trigger the mouseup handling in edge cases where we release the mouse button
      // but `mouseup` isn't triggered, for example outside the iframe/window.
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      if (event.buttons !== 1) endTracking(false);
    }

    function onMouseUp(event: MouseEvent): void {
      // Only a release inside the grid is followed by a `click` on a cell;
      // suppressing after an outside release would eat the next legitimate click.
      endTracking(
        didDragRef.current && event.target instanceof Node && gridRef.current?.contains(event.target) === true,
      );
    }

    function endTracking(suppressClick: boolean): void {
      window.removeEventListener('mouseover', onMouseOver);
      window.removeEventListener('mouseup', onMouseUp);
      dragStartRef.current = null;
      setIsRangeDragging(false);
      suppressClickRef.current = suppressClick;
    }
  }

  const handleCellMouseDown = useLatestFunc((position: Position, event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    didDragRef.current = false;
    suppressClickRef.current = false;
    if (event.shiftKey) {
      if (selectedPosition.mode !== 'SELECT' || !selectedCellIsWithinViewportBounds) return;
      // Extend from the active cell; keep focus (and a continued drag) anchored on it
      event.preventDefault();
      dragStartRef.current = { idx: anchorIdx, rowIdx: anchorRowIdx };
      didDragRef.current = true;
      setIsRangeDragging(true);
      setRangeHead(position);
      startWindowMouseTracking();
    } else {
      // Arm a potential drag without changing any state, so an unmoved mouseup
      // stays indistinguishable from a plain click
      dragStartRef.current = position;
      startWindowMouseTracking();
    }
  });

  const handleCellMouseEnter = useLatestFunc((position: Position) => {
    const start = dragStartRef.current;
    if (start === null) return;
    if (!didDragRef.current) {
      if (position.idx === start.idx && position.rowIdx === start.rowIdx) return;
      // First entry into another cell: the gesture is a drag, not a click
      didDragRef.current = true;
      setIsRangeDragging(true);
      // Drop any text selection the unprevented mousedown started in the start cell
      window.getSelection()?.removeAllRanges();
      selectCell(start, false, false);
    }
    setRangeHead(position);
  });

  const handleClickCapture = useLatestFunc((event: React.MouseEvent<HTMLDivElement>) => {
    // A drag released over the start cell (or a shift+click) still fires a `click`
    // that would select/edit the cell and collapse the range — swallow it once
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  });

  // Returns whether the key was handled. Plain function on purpose: useLatestFunc
  // discards return values, and only handleKeyDown calls this.
  function handleShiftArrowKey(key: string): boolean {
    if (!enabled || selectedPosition.mode !== 'SELECT' || !selectedCellIsWithinViewportBounds) return false;
    const head = resolvedHead ?? { idx: anchorIdx, rowIdx: anchorRowIdx };
    let { idx, rowIdx } = head;
    switch (key) {
      case 'ArrowUp':
        rowIdx--;
        break;
      case 'ArrowDown':
        rowIdx++;
        break;
      case 'ArrowLeft':
        idx--;
        break;
      case 'ArrowRight':
        idx++;
        break;
    }
    idx = max(0, min(idx, maxColIdx));
    rowIdx = max(0, min(rowIdx, rows.length - 1));
    if (idx !== head.idx || rowIdx !== head.rowIdx) {
      setRangeHead({ idx, rowIdx });
      scrollToCell({ idx, rowIdx });
    }
    // handled even when clamped at the grid edge: falling through to navigation would collapse the range
    return true;
  }

  return {
    normalizedRange,
    isRangeDragging,
    collapseRange,
    handleCellMouseDown: enabled ? handleCellMouseDown : undefined,
    handleCellMouseEnter: enabled ? handleCellMouseEnter : undefined,
    handleClickCapture: enabled ? handleClickCapture : undefined,
    handleShiftArrowKey,
  } as const;
}
